import { useEarningsFilter } from '@terminal/react-query-hooks/earnings/useEarningsFilter';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useGraphRelations } from '@terminal/react-query-hooks/graph/useGraphRelations';
import { parseGraphNodeKey } from '@terminal/views/graph/utils/graph-types/graph-utils';
import { useEffect, useMemo } from 'react';

import { extractReportingPeriods, getSelectableDateRange } from './utils';

export const useThemeEarningsData = (themeId: string) => {
  const {
    data: relationsData,
    isError: isThemeCompaniesError,
    isFetching: isThemeCompaniesFetching,
    error: themeCompaniesError,
  } = useGraphRelations({
    nodeType: 'theme',
    node: themeId,
    relationKind: 'theme-company',
    direction: 'outbound',
  });

  const themeCompaniesData = useMemo(
    () =>
      relationsData?.edges.map(edge => ({
        company: parseGraphNodeKey(edge.target).rawId,
        rank: edge.attributes.rank,
      })) ?? [],
    [relationsData?.edges]
  );
  const entityTags = themeCompaniesData.map(e => e.company);

  const { data: entitiesResult, isLoading: isEntitiesLoading } = useEntities({
    entityTags,
  });

  const { startDate, endDate } = getSelectableDateRange();

  const foundEntityTags = isEntitiesLoading
    ? []
    : entitiesResult.map(e => e.tag);

  const {
    data: earningsQueryResult,
    isError: isEarningsError,
    isFetching: isEarningsFetching,
    error: earningsError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useEarningsFilter({
    body: {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      entities: foundEntityTags,
    },
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isEarningsError) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isEarningsError, fetchNextPage]);

  const isError = isEarningsError || isThemeCompaniesError;
  const error = earningsError ?? themeCompaniesError;
  const isLoading =
    isEarningsFetching ||
    hasNextPage ||
    isThemeCompaniesFetching ||
    isEntitiesLoading;

  const earningsData = useMemo(() => {
    const ranksByEntityTag = new Map(
      themeCompaniesData.map(company => [company.company, company.rank])
    );

    return (
      earningsQueryResult?.pages.flat().map(earning => ({
        ...earning,
        rank: ranksByEntityTag.get(earning.entity_tag) ?? earning.rank,
      })) ?? []
    );
  }, [earningsQueryResult?.pages, themeCompaniesData]);

  const reportingPeriods = useMemo(
    () => extractReportingPeriods(earningsData),
    [earningsData]
  );

  return {
    earningsData,
    reportingPeriods,
    isError,
    error,
    isLoading,
  };
};
